import { useEffect, useMemo, type ReactNode } from 'react';
import { DirectionProvider, MantineProvider, localStorageColorSchemeManager, useDirection } from '@mantine/core';
import { DatesProvider } from '@mantine/dates';
import { ModalsProvider } from '@mantine/modals';
import { Notifications } from '@mantine/notifications';
import { QueryClientProvider } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { RouterProvider, createBrowserRouter } from 'react-router-dom';
import 'dayjs/locale/ar-sa';
import { AuthProvider } from '@web/auth/AuthProvider';
import { directionOf } from '@web/i18n';
import { queryClient } from '@web/lib/query';
import { ShellContext } from '@web/shells/context';
import type { ShellResolution } from '@web/shells/detect';
import { buildRoutes } from '@web/shells/routes';
import { createKfmsTheme, type ShellKind } from '@web/theme/theme';
import { DemoBanner } from '@web/ui/DemoBanner';
import { UpdateNotice } from '@web/ui/UpdateNotice';
import { updateReady } from '@web/lib/pwaUpdate';

const colorSchemeManager = localStorageColorSchemeManager({ key: 'kfms.colorScheme' });

/** Keeps Mantine's direction in sync with the UI language. */
function DirectionSync({ dir }: { dir: 'rtl' | 'ltr' }) {
  const { setDirection } = useDirection();
  useEffect(() => setDirection(dir), [dir, setDirection]);
  return null;
}

function ThemedProviders({ shell, children }: { shell: ShellKind; children: ReactNode }) {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const dir = directionOf(lang);
  const theme = useMemo(() => createKfmsTheme({ lang, shell }), [lang, shell]);
  return (
    <DirectionProvider initialDirection={dir} detectDirection={false}>
      <DirectionSync dir={dir} />
      <MantineProvider theme={theme} colorSchemeManager={colorSchemeManager} defaultColorScheme="light">
        <DatesProvider settings={{ locale: lang === 'ar' ? 'ar-sa' : 'en', firstDayOfWeek: 0, weekendDays: [5, 6] }}>
          <Notifications position={dir === 'rtl' ? 'top-left' : 'top-right'} limit={4} />
          <DemoBanner />
          <UpdateNotice />
          {children}
        </DatesProvider>
      </MantineProvider>
    </DirectionProvider>
  );
}

/** Root component: providers + the router of the detected shell. */
export function App({ resolution }: { resolution: ShellResolution }) {
  const router = useMemo(
    () => createBrowserRouter(buildRoutes(resolution.shell), { basename: resolution.basename || undefined }),
    [resolution],
  );
  // a newer version is ready: the next in-app navigation becomes a full page load of the new version
  useEffect(() => {
    let lastKey = router.state.location.key;
    return router.subscribe((state) => {
      const moved = state.location.key !== lastKey;
      lastKey = state.location.key;
      const to = state.navigation.location ?? (moved ? state.location : null);
      if (!to || !updateReady()) return;
      // the data router's locations already carry the basename
      const base = resolution.basename ?? '';
      const path = base && to.pathname !== base && !to.pathname.startsWith(`${base}/`) ? `${base}${to.pathname}` : to.pathname;
      window.location.assign(`${path}${to.search}${to.hash}`);
    });
  }, [router, resolution.basename]);
  return (
    <ShellContext.Provider value={resolution}>
      <QueryClientProvider client={queryClient}>
        <ThemedProviders shell={resolution.shell}>
          <AuthProvider>
            <ModalsProvider>
              <RouterProvider router={router} />
            </ModalsProvider>
          </AuthProvider>
        </ThemedProviders>
      </QueryClientProvider>
    </ShellContext.Provider>
  );
}
